import { DESTINATION_TYPES } from "@/react/features/support-tools/extensions/constants";
import { useSelector } from "react-redux";
import { pbxIdSelector } from "@/react/features/customer/customerSlice";
import { useGetAvailableExtensionsQuery } from "@/react/features/support-tools/extensions/extensionsApi";
import React, { useMemo } from "react";
import DynamicSelectCell from "@/react/features/smp/cells/DynamicSelectCell";
import {
  EditableStringCell,
  ValidationWrapper,
} from "@/react/components/table/components";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";
import { useDestinationCell } from "@/react/features/support-tools/extensions/forwarding-rules/cells/useDestinationCell";
import * as yup from "yup";
import schema from "@/react/features/support-tools/extensions/forwarding-rules/schema";

const serviceTypeToDestinationTypeEquality = (
  extensionServiceType,
  destinationServiceType,
  destinationType
) => {
  if (destinationType === DESTINATION_TYPES.AUTO_ATTENDANT) {
    return destinationServiceType === DESTINATION_TYPES.AUTO_ATTENDANT;
  }

  if (destinationType === DESTINATION_TYPES.EXTENSION) {
    if (
      extensionServiceType === "RING_GROUP" ||
      extensionServiceType === "CALL_QUEUE"
    )
      return destinationServiceType === "EXTENSION";

    return ["EXTENSION", "CALL_QUEUE", "RING_GROUP"].includes(
      destinationServiceType
    );
  }
};

export const DestinationEditableCell = (rule) => (props) => {
  const pbxId = useSelector(pbxIdSelector);
  const { errors, destinationType, value } = useDestinationCell(props, rule);

  // This can be called when selecting the three dot menu for a column,
  // in which case there is no row
  if (!props.row) {
    return value;
  }

  const serviceTypeCell = props.row
    ?.getAllCells()
    .find(
      (c) => c.column.id === yup.reach(schema, `extension.serviceType`).spec.key
    )
    ?.getContext();

  const { data: extensions, isFetching } =
    useGetAvailableExtensionsQuery(pbxId);

  const options = useMemo(
    () =>
      (extensions || [])
        .filter((extension) =>
          serviceTypeToDestinationTypeEquality(
            serviceTypeCell.getValue(),
            extension.serviceType,
            destinationType
          )
        )
        .map((extension) => ({
          value: extension.extensionNumber,
          label: extension.extensionNumber,
        })),
    [extensions, destinationType]
  );

  if (destinationType === DESTINATION_TYPES.VOICEMAIL) {
    return (
      <FullWidthDiv>
        <ValidationWrapper errors={errors}>555</ValidationWrapper>
      </FullWidthDiv>
    );
  }

  if (
    [DESTINATION_TYPES.AUTO_ATTENDANT, DESTINATION_TYPES.EXTENSION].includes(
      destinationType
    )
  ) {
    return (
      <DynamicSelectCell
        {...props}
        options={options}
        isFetching={isFetching}
        testId={`${rule}-destination-select`}
        placeholder={"Select Destination"}
      />
    );
  }

  if (destinationType === DESTINATION_TYPES.EXTERNAL_NUMBER) {
    return <EditableStringCell {...props} />;
  }

  if (destinationType === DESTINATION_TYPES.DROP_CALL) {
    return null;
  }

  if (value) {
    return value;
  }
  return "No destination type provided";
};
